import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useSearchParams } from 'react-router-dom';

import {
  DEFAULT_FILTERS,
  filtersFromSearchParams,
  filtersToSearchParams,
  loadStoredFilters,
  storeFilters,
  type FeedFilters,
} from './filters';

export interface FeedFiltersApi {
  filters: FeedFilters;
  setFilters: (next: Partial<FeedFilters>) => void;
  toggleType: (type: FeedFilters['types'][number]) => void;
  toggleScope: (scope: FeedFilters['scopes'][number]) => void;
  toggleTag: (tag: string) => void;
  clear: () => void;
}

interface Options {
  /**
   * Restore persisted filters into the URL when it carries none.
   *
   * Only the feed itself opts in. The sidebar reads the same state but must not
   * seed, or opening a post would rewrite its URL to
   * `/posts/abc?country=RO&type=BAD` — filter params that mean nothing on a
   * post page and travel into any link the reader copies.
   */
  seedFromStorage?: boolean;
}

/**
 * Filter state, owned by the URL.
 *
 * The URL is the single source of truth (spec §6.1: shareable), with
 * localStorage used only to seed it on a first visit to a bare `/` — so a link
 * someone sends always wins over what the recipient last had selected.
 */
export function useFeedFilters({ seedFromStorage = false }: Options = {}): FeedFiltersApi {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);

  // Seeding runs at most once per mount, and only when the URL says nothing.
  const seeded = useRef(false);
  useEffect(() => {
    if (!seedFromStorage || seeded.current) return;
    seeded.current = true;

    if (searchParams.toString() !== '') return;

    const stored = loadStoredFilters();
    if (!stored) return;

    const restored = { ...DEFAULT_FILTERS, ...stored };
    const next = filtersToSearchParams(restored);
    if (next.toString() !== '') setSearchParams(next, { replace: true });
  }, [seedFromStorage, searchParams, setSearchParams]);

  useEffect(() => {
    // Only the feed's own view of the filters is worth persisting; the sidebar
    // renders the same state and would write it a second time for nothing.
    if (seedFromStorage) storeFilters(filters);
  }, [seedFromStorage, filters]);

  const setFilters = useCallback(
    (partial: Partial<FeedFilters>) => {
      const next = { ...filters, ...partial };
      // Changing country invalidates whichever town was selected under the old one.
      if (partial.country !== undefined && partial.country !== filters.country) {
        next.town = partial.town ?? '';
      }
      setSearchParams(filtersToSearchParams(next), { replace: false });
    },
    [filters, setSearchParams],
  );

  const toggleType = useCallback(
    (type: FeedFilters['types'][number]) => {
      const next = filters.types.includes(type)
        ? filters.types.filter((item) => item !== type)
        : [...filters.types, type];

      // Deselecting the last register would show an empty feed with no
      // explanation; turning them all back on is the useful reading of that tap.
      setFilters({ types: next.length === 0 ? [...DEFAULT_FILTERS.types] : next });
    },
    [filters.types, setFilters],
  );

  const toggleScope = useCallback(
    (scope: FeedFilters['scopes'][number]) => {
      const next = filters.scopes.includes(scope)
        ? filters.scopes.filter((item) => item !== scope)
        : [...filters.scopes, scope];

      setFilters({ scopes: next.length === 0 ? [...DEFAULT_FILTERS.scopes] : next });
    },
    [filters.scopes, setFilters],
  );

  const toggleTag = useCallback(
    (tag: string) => {
      const next = filters.tags.includes(tag)
        ? filters.tags.filter((item) => item !== tag)
        : [...filters.tags, tag];
      setFilters({ tags: next });
    },
    [filters.tags, setFilters],
  );

  const clear = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: false });
  }, [setSearchParams]);

  return { filters, setFilters, toggleType, toggleScope, toggleTag, clear };
}
